import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';

/** A rendered slot: either a page number to jump to, or a gap. */
export interface BzPaginationItem {
  readonly page: number | null;
  readonly key: string;
}

/**
 * Page navigation.
 *
 * `page` is **1-based**, matching what the control displays. The legacy
 * console's component took a 0-based `pageIndex` while rendering 1-based
 * labels, which is a reliable source of off-by-one bugs at every call site.
 *
 * Written from scratch rather than wrapping `ngb-pagination` as the legacy
 * component did — the design system does not depend on ng-bootstrap, and
 * three of the four consuming applications would otherwise have to keep it
 * installed for this one control.
 */
@Component({
  selector: 'bz-pagination',
  templateUrl: './bz-pagination.html',
  styleUrl: './bz-pagination.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzPagination {
  /** 1-based. */
  readonly page = model(1);

  readonly totalItems = input(0);
  readonly pageSize = input(10);

  /** How many numbered pages to show around the current one. */
  readonly siblingCount = input(1);

  readonly ariaLabel = input('Pagination');

  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));

  /**
   * `page` as the control actually uses it. A consumer is free to bind a
   * value outside the range — a stale deep link, a filter that shrank the
   * result set — and everything here works off the clamped value rather than
   * rendering a control whose highlighted page does not exist.
   */
  readonly currentPage = computed(() => this.clamp(this.page()));

  protected readonly items = computed<readonly BzPaginationItem[]>(() => {
    const total = this.pageCount();
    const current = this.currentPage();
    const siblings = Math.max(0, this.siblingCount());

    // First and last are always shown, so the window only has to cover the
    // pages around the current one.
    const windowStart = Math.max(2, current - siblings);
    const windowEnd = Math.min(total - 1, current + siblings);

    const pages = new Set<number>([1, total]);
    for (let page = windowStart; page <= windowEnd; page++) {
      pages.add(page);
    }

    const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);

    const items: BzPaginationItem[] = [];
    sorted.forEach((page, index) => {
      const previous = sorted[index - 1];
      // A gap of exactly one page is rendered as that page rather than an
      // ellipsis — an "…" hiding a single number is worse than the number.
      if (previous !== undefined && page - previous === 2) {
        items.push({ page: previous + 1, key: `p${previous + 1}` });
      } else if (previous !== undefined && page - previous > 2) {
        items.push({ page: null, key: `gap-${previous}` });
      }
      items.push({ page, key: `p${page}` });
    });

    return items;
  });

  protected goTo(page: number): void {
    const next = this.clamp(page);
    if (next !== this.page()) {
      this.page.set(next);
    }
  }

  private clamp(page: number): number {
    return Math.min(Math.max(1, Math.trunc(page) || 1), this.pageCount());
  }
}
